import React, { FC, createContext, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RouteComponentProps, useParams } from "react-router";
import { useHistory } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import { NEW_WAY_TO_ENCODING_FILTER_SIGN } from "go-list/core/components/Filter/services";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import { apiOrganization } from "../../../../../../../../../../services/Api/Organization/apiOrganization";
import {
	PurchaseApi,
	PurchaseCorrectionApi,
	PurchaseReceiveApi,
} from "../../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../../services/Api/api";
import {
	TransactionContext,
	TransactionResourceType,
} from "../../../../../../components/Transaction/Context/TransactionContext";
import PurchasePreview from "../../components/PurchasePreview/PurchasePreview";

interface MatchParams {
	purchaseId: string;
}

interface PurchaseContextType {
	purchase: PurchaseApi;
	setPurchase: (p: PurchaseApi) => void;
	fetchPurchase: () => Promise<void>;
	purchaseReceives?: PurchaseReceiveApi[];
	fetchPurchaseReceives: () => Promise<void>;
	fetchPurchaseCorrections: () => Promise<void>;
	customFieldsConfig?: CustomFieldTemplateApi[];
	purchaseCorrections?: PurchaseCorrectionApi[];
	purchaseCorrectionCustomFieldsConfig?: CustomFieldTemplateApi[];
}

export const PurchaseContext = createContext<PurchaseContextType>({
	purchase: {} as PurchaseApi,
	setPurchase: (purchase: PurchaseApi) => {},
	fetchPurchase: async () => {},
	purchaseReceives: [] as PurchaseReceiveApi[],
	fetchPurchaseReceives: async () => {},
	fetchPurchaseCorrections: async () => {},
	customFieldsConfig: [],
});

const OrganizationOrderPurchasePreviewPage: FC<RouteComponentProps> = (props) => {
	const [purchase, setPurchase] = useState<PurchaseApi | undefined>(undefined);
	const [purchaseReceives, setPurchaseReceives] = useState<PurchaseReceiveApi[] | undefined>(undefined);
	const [purchaseCorrections, setPurchaseCorrections] = useState<PurchaseCorrectionApi[] | undefined>();
	const [purchaseCorrectionCustomFieldsConfig, setPurchaseCorrectionCustomFieldsConfig] = useState<
		CustomFieldTemplateApi[] | undefined
	>();
	const [customFieldsConfig, setCustomFieldsConfig] = useState<CustomFieldTemplateApi[] | undefined>(undefined);
	const { addFlash } = useFlash();
	const { purchaseId } = useParams<MatchParams>();
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const { t } = useTranslation();
	const { setListItemId, setTransactionResourceType } = useContext(TransactionContext);
	const history = useHistory();

	useEffect(() => {
		onFetchPurchase();
		setListItemId(purchaseId);
		setTransactionResourceType(TransactionResourceType.PURCHASE);
	}, []);

	const onFetchPurchase = async () => {
		try {
			const params = { include: "all" };
			const customFieldsParams = {
				f: btoa(`${NEW_WAY_TO_ENCODING_FILTER_SIGN}resource_type|e=PURCHASE_ORDER`),
				include: "options,constraints",
			};

			const requests: [Promise<PurchaseApi>, Promise<CustomFieldTemplateApi[]>] = [
				api.organization().getPurchase(purchaseId, params),
				api.organization().getCustomFields(customFieldsParams),
			];
			const [newPurchase, newCustomFieldsConfig] = await Promise.all(requests);
			setPurchase(newPurchase);
			setCustomFieldsConfig(newCustomFieldsConfig);
			handleChangeTabTitle(`${newPurchase.document_number} | ${t("modules.purchase.header.title")}`);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const onFetchPurchaseReceives = async () => {
		try {
			if (purchase?.id) {
				const params = { include: "items,items.product,items.comment" };
				const res = await api.organization().getPurchaseReceives(purchase.id, params);
				setPurchaseReceives(res);
			}
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const fetchPurchaseCorrections = async () => {
		if (!purchase?.id) return;

		try {
			const customFieldsParams = {
				f: btoa(`${NEW_WAY_TO_ENCODING_FILTER_SIGN}resource_type|e=PURCHASE_CORRECTION`),
				include: "options,constraints",
			};
			const requests: [Promise<PurchaseCorrectionApi[]>, Promise<CustomFieldTemplateApi[]>] = [
				apiOrganization.getPurchaseCorrections(purchase.id, {
					sort: "-corrected_at",
					include: "items,items.product",
				}),
				apiOrganization.getCustomFields(customFieldsParams),
			];
			const [purchaseCorrectionsResponse, customFieldsResponse] = await Promise.all(requests);
			setPurchaseCorrections(purchaseCorrectionsResponse);
			setPurchaseCorrectionCustomFieldsConfig(customFieldsResponse);
			if (location.pathname.endsWith("corrections")) {
				if (purchaseCorrectionsResponse.length > 0) {
					const correctionId = purchaseCorrectionsResponse[purchaseCorrectionsResponse.length - 1].id;
					history.replace(`${props.location.pathname}/${correctionId}`);
				} else {
					history.replace(`${props.location.pathname}/new`);
				}
			}
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	if (!purchase?.id) return <LoadingContainer />;

	return (
		<PurchaseContext.Provider
			value={{
				purchase,
				setPurchase,
				fetchPurchase: onFetchPurchase,
				purchaseReceives,
				fetchPurchaseReceives: onFetchPurchaseReceives,
				customFieldsConfig,
				fetchPurchaseCorrections,
				purchaseCorrections,
				purchaseCorrectionCustomFieldsConfig,
			}}
		>
			<PurchasePreview url={props.match.url} />
		</PurchaseContext.Provider>
	);
};

export default OrganizationOrderPurchasePreviewPage;
